import { Tooltip } from 'antd';
import React from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as ClipboardListIcon } from '../../../assets/icons/SVG/clipboard-list.svg';
import { ReactComponent as WhistleSolidIcon } from '../../../assets/icons/SVG/whistle-solid.svg';
import ReplaceStrings from '../../../config/replaceStrings';
import Routes from '../../../config/routes';

export interface MatchTableActionsProps {
    matchId: number;
    matchLogLabel: string;
    /** When true, show whistle / details actions */
    canUseMatchActions: boolean;
    /** When true, show Run game / View Game link (may differ from details access on official’s match list) */
    showRunGameLink?: boolean;
    onOfficialsClick: () => void;
    /** When set (user can update match), opens edit drawer; otherwise `detailsTo` link is used */
    onDetailsClick?: () => void;
    detailsTo: string;
}

/**
 * Shared actions for match rows: officials (whistle), details (list icon), run/view game link.
 * Icons: assets/icons/SVG whistle-solid, clipboard-list.
 */
const MatchTableActions: React.FC<MatchTableActionsProps> = ({
    matchId,
    matchLogLabel,
    canUseMatchActions,
    showRunGameLink = true,
    onOfficialsClick,
    onDetailsClick,
    detailsTo,
}) => {
    if (!canUseMatchActions && !showRunGameLink) {
        return <span />;
    }

    return (
        <div className="match-table-actions">
            {canUseMatchActions && (
                <>
                    <Tooltip title="Officials" mouseEnterDelay={0}>
                        <span
                            className="match-table-actions__icon-link"
                            role="button"
                            tabIndex={0}
                            aria-label="Officials"
                            onClick={(): void => onOfficialsClick()}
                            onKeyDown={(e: React.KeyboardEvent): void => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onOfficialsClick();
                                }
                            }}
                        >
                            <WhistleSolidIcon className="table-action-svg-icon" aria-hidden />
                        </span>
                    </Tooltip>
                    <span className="match-table-actions__divider" aria-hidden="true" />
                    <Tooltip title="Details" mouseEnterDelay={0}>
                        <span className="match-table-actions__icon-tooltip-wrap">
                            {onDetailsClick ? (
                                <span
                                    className="match-table-actions__icon-link"
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Details"
                                    onClick={(): void => onDetailsClick()}
                                    onKeyDown={(e: React.KeyboardEvent): void => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onDetailsClick();
                                        }
                                    }}
                                >
                                    <ClipboardListIcon
                                        className="table-action-svg-icon"
                                        aria-hidden
                                    />
                                </span>
                            ) : (
                                <Link
                                    className="match-table-actions__icon-link"
                                    to={detailsTo}
                                    aria-label="Details"
                                >
                                    <ClipboardListIcon
                                        className="table-action-svg-icon"
                                        aria-hidden
                                    />
                                </Link>
                            )}
                        </span>
                    </Tooltip>
                </>
            )}
            {canUseMatchActions && showRunGameLink && (
                <span className="match-table-actions__divider" aria-hidden="true" />
            )}
            {showRunGameLink && (
                <Link
                    className="match-table-actions__log-link"
                    to={Routes.ROUTE_MATCH_LOG.replace(ReplaceStrings.MATCH_ID, matchId.toString())}
                    title={matchLogLabel}
                    aria-label={matchLogLabel}
                >
                    {matchLogLabel}
                </Link>
            )}
        </div>
    );
};

export default MatchTableActions;
